import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Calendar, Clock, MapPin, User as UserIcon, Phone, Shirt, FileText, MessageCircle, CheckCircle2,
  Loader2, Send, ArrowLeft, Star, Sparkles, ImagePlus,
} from "lucide-react";
import { toast } from "sonner";

export default function MissionDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mission, setMission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [editingSheet, setEditingSheet] = useState(false);
  const [sheet, setSheet] = useState({});
  const [savingSheet, setSavingSheet] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const isPro = (user?.role === "creator" || user?.role === "professional");
  const isTalent = user?.role === "talent";

  const loadMission = async () => {
    try {
      const { data } = await api.get(`/missions/${id}`);
      setMission(data);
      setSheet({
        mission_date: (data.mission_date || "").slice(0, 10),
        mission_time: data.mission_time || "",
        location: data.location || "",
        contact_name: data.contact_name || "",
        contact_phone: data.contact_phone || "",
        dress_code: data.dress_code || "",
        instructions: data.instructions || "",
        notes: data.notes || "",
      });
    } catch {
      toast.error("Mission introuvable");
      navigate(isPro ? "/createurs" : "/talent");
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const { data } = await api.get(`/missions/${id}/messages`);
      setMessages(Array.isArray(data) ? data : []);
    } catch { /* noop */ }
  };

  const loadReviews = async () => {
    try {
      const { data } = await api.get(`/missions/${id}/reviews`);
      setReviews(Array.isArray(data) ? data : []);
    } catch { /* noop */ }
  };

  useEffect(() => {
    loadMission();
    loadMessages();
    loadReviews();
    pollRef.current = setInterval(() => { loadMessages(); }, 6000);
    return () => clearInterval(pollRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const sendMsg = async (e) => {
    e?.preventDefault?.();
    if (!draft.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/missions/${id}/messages`, { body: draft.trim() });
      setMessages((prev) => [...prev, data]);
      setDraft("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSending(false);
    }
  };

  const saveSheet = async () => {
    setSavingSheet(true);
    try {
      const payload = Object.fromEntries(Object.entries(sheet).filter(([, v]) => v !== "" && v != null));
      await api.put(`/missions/${id}`, payload);
      toast.success("Fiche mission mise à jour");
      setEditingSheet(false);
      await loadMission();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSavingSheet(false);
    }
  };

  const confirmComplete = async () => {
    try {
      const { data } = await api.post(`/missions/${id}/complete`);
      setMission(data);
      if (data.status === "completed") {
        toast.success("Mission terminée — pensez à laisser un avis !");
      } else {
        toast.success("Confirmation enregistrée — en attente de l'autre partie.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const myReviewSent = useMemo(() => {
    if (!mission || !user) return false;
    if (isTalent) return !!mission.talent_review_id;
    if (isPro) return !!mission.pro_review_id;
    return false;
  }, [mission, user, isTalent, isPro]);

  if (loading || !mission) {
    return (
      <AppShell>
        <div className="max-w-4xl mx-auto px-6 py-24 text-center text-[#8B8680]">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </div>
      </AppShell>
    );
  }

  const other = isTalent ? mission.pro : mission.talent;
  const otherCompany = isTalent ? mission.pro_profile?.company_name : null;
  const stage = mission.status; // scheduled | reminded | completed | cancelled
  const isLocked = stage === "completed" || stage === "cancelled";
  const myConfirmed = isTalent ? mission.talent_confirmed_completion : mission.pro_confirmed_completion;
  const otherConfirmed = isTalent ? mission.pro_confirmed_completion : mission.talent_confirmed_completion;

  return (
    <AppShell>
      <div data-testid="mission-detail-page" className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        <button onClick={() => navigate(-1)} className="caption-label text-[#A26F3E] hover:text-[#1A1A1A] mb-4 inline-flex items-center gap-1.5">
          <ArrowLeft className="w-3 h-3" /> Retour
        </button>
        <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
          <div className="min-w-0">
            <p className="caption-label text-[#A26F3E]">Mission active</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-1 leading-tight">{mission.title}</h1>
            <div className="flex items-center gap-3 mt-3">
              <div className="w-9 h-9 rounded-full bg-[#FAF3EA] border border-[#EADDC9] flex items-center justify-center overflow-hidden">
                {other?.picture ? (
                  <img src={other.picture} alt={other.name} className="w-full h-full object-cover" />
                ) : <UserIcon className="w-4 h-4 text-[#A26F3E]" />}
              </div>
              <div>
                <p className="text-sm font-semibold text-[#1A1A1A]">{otherCompany || other?.name}</p>
                <p className="text-xs text-[#8C7D6B]">{isTalent ? "Marque" : "Talent"}</p>
              </div>
            </div>
          </div>
          <StageBadge stage={stage} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Mission sheet */}
          <section className="lg:col-span-2 bg-white rounded-3xl border border-[#EADDC9] p-7" data-testid="mission-sheet">
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="caption-label text-[#A26F3E]">Fiche mission</p>
                <h2 className="font-serif text-2xl text-[#1A1A1A] mt-0.5">Détails de la mission</h2>
              </div>
              {isPro && !isLocked && !editingSheet && (
                <Button variant="outline" onClick={() => setEditingSheet(true)} className="rounded-full border-[#1A1A1A]" data-testid="mission-edit-btn">
                  Modifier
                </Button>
              )}
            </div>

            {editingSheet ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="mission-edit-form">
                <SheetField label="Date" icon={Calendar}>
                  <Input type="date" value={sheet.mission_date} onChange={(e) => setSheet({ ...sheet, mission_date: e.target.value })} className="rounded-xl mt-2 h-11" data-testid="sheet-date" />
                </SheetField>
                <SheetField label="Heure" icon={Clock}>
                  <Input value={sheet.mission_time} onChange={(e) => setSheet({ ...sheet, mission_time: e.target.value })} placeholder="14h00" className="rounded-xl mt-2 h-11" data-testid="sheet-time" />
                </SheetField>
                <SheetField label="Lieu" icon={MapPin}>
                  <Input value={sheet.location} onChange={(e) => setSheet({ ...sheet, location: e.target.value })} placeholder="Adresse" className="rounded-xl mt-2 h-11" data-testid="sheet-location" />
                </SheetField>
                <SheetField label="Contact" icon={UserIcon}>
                  <Input value={sheet.contact_name} onChange={(e) => setSheet({ ...sheet, contact_name: e.target.value })} placeholder="Nom du référent" className="rounded-xl mt-2 h-11" data-testid="sheet-contact-name" />
                </SheetField>
                <SheetField label="Téléphone contact" icon={Phone}>
                  <Input value={sheet.contact_phone} onChange={(e) => setSheet({ ...sheet, contact_phone: e.target.value })} placeholder="+33…" className="rounded-xl mt-2 h-11" data-testid="sheet-contact-phone" />
                </SheetField>
                <SheetField label="Dress code" icon={Shirt}>
                  <Input value={sheet.dress_code} onChange={(e) => setSheet({ ...sheet, dress_code: e.target.value })} placeholder="Total look noir, …" className="rounded-xl mt-2 h-11" data-testid="sheet-dress-code" />
                </SheetField>
                <div className="sm:col-span-2">
                  <SheetField label="Instructions" icon={FileText}>
                    <Textarea value={sheet.instructions} onChange={(e) => setSheet({ ...sheet, instructions: e.target.value })} rows={3} className="rounded-xl mt-2" data-testid="sheet-instructions" />
                  </SheetField>
                </div>
                <div className="sm:col-span-2">
                  <SheetField label="Notes internes" icon={FileText}>
                    <Textarea value={sheet.notes} onChange={(e) => setSheet({ ...sheet, notes: e.target.value })} rows={2} className="rounded-xl mt-2" data-testid="sheet-notes" />
                  </SheetField>
                </div>
                <div className="sm:col-span-2 flex gap-2 mt-2">
                  <Button onClick={saveSheet} disabled={savingSheet} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white" data-testid="sheet-save-btn">
                    {savingSheet ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
                  </Button>
                  <Button variant="outline" onClick={() => setEditingSheet(false)} className="rounded-full">Annuler</Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                <SheetReadField icon={Calendar} label="Date" value={mission.mission_date ? new Date(mission.mission_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "—"} />
                <SheetReadField icon={Clock} label="Heure" value={mission.mission_time || "—"} />
                <SheetReadField icon={MapPin} label="Lieu" value={mission.location || "—"} />
                <SheetReadField icon={UserIcon} label="Contact" value={mission.contact_name || "—"} />
                <SheetReadField icon={Phone} label="Téléphone" value={mission.contact_phone || "—"} />
                <SheetReadField icon={Shirt} label="Dress code" value={mission.dress_code || "—"} />
                {mission.instructions && (
                  <div className="sm:col-span-2 p-4 rounded-2xl bg-[#FAF3EA] border border-[#EADDC9]">
                    <p className="caption-label text-[#A26F3E] mb-1.5">Instructions</p>
                    <p className="text-sm text-[#3C2C1D] whitespace-pre-wrap leading-relaxed">{mission.instructions}</p>
                  </div>
                )}
              </div>
            )}

            {!isLocked && (
              <div className="mt-7 pt-6 border-t border-[#FAF3EA] flex flex-wrap items-center gap-3">
                <Button
                  onClick={confirmComplete}
                  disabled={myConfirmed}
                  className="rounded-full bg-[#1A1A1A] hover:bg-[#2A1E12] text-white disabled:opacity-60"
                  data-testid="mission-complete-btn"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  {myConfirmed ? (otherConfirmed ? "Mission terminée" : "En attente de l'autre partie") : "Confirmer mission terminée"}
                </Button>
                {myConfirmed && !otherConfirmed && (
                  <p className="text-xs text-[#8C7D6B]">L'autre partie doit aussi confirmer.</p>
                )}
              </div>
            )}

            {stage === "completed" && (
              <div className="mt-7 pt-6 border-t border-[#FAF3EA] flex flex-wrap items-center gap-3" data-testid="mission-after-block">
                {!myReviewSent ? (
                  <Button onClick={() => setReviewOpen(true)} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white" data-testid="open-review-btn">
                    <Star className="w-4 h-4 mr-2" /> Laisser un avis
                  </Button>
                ) : (
                  <span className="caption-label text-[#A26F3E]">Avis envoyé · merci !</span>
                )}
                {isTalent && (
                  <Button onClick={() => setPortfolioOpen(true)} variant="outline" className="rounded-full border-[#1A1A1A]" data-testid="add-to-portfolio-btn">
                    <ImagePlus className="w-4 h-4 mr-2" /> Ajouter au portfolio
                  </Button>
                )}
              </div>
            )}

            {reviews.length > 0 && (
              <div className="mt-6 space-y-3">
                {reviews.map((r) => (
                  <div key={r.id} className="rounded-2xl border border-[#EADDC9] p-4 bg-[#FFFBF5]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        {[1,2,3,4,5].map((n) => (
                          <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "fill-[#D4A574] text-[#D4A574]" : "text-[#EADDC9]"}`} />
                        ))}
                      </div>
                      <p className="caption-label text-[#A26F3E]">
                        {r.direction === "talent_on_pro" ? "Talent → Marque" : "Marque → Talent"}
                      </p>
                    </div>
                    {r.comment && <p className="text-sm text-[#3C2C1D] mt-2 italic">« {r.comment} »</p>}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Chat */}
          <section className="bg-white rounded-3xl border border-[#EADDC9] p-5 flex flex-col h-[640px]" data-testid="mission-chat">
            <div className="flex items-center gap-2 pb-3 border-b border-[#FAF3EA]">
              <MessageCircle className="w-4 h-4 text-[#C68E59]" />
              <p className="font-semibold text-[#1A1A1A]">Conversation privée</p>
            </div>
            <div className="flex-1 overflow-y-auto py-4 space-y-3" data-testid="messages-list">
              {messages.length === 0 && (
                <p className="text-center text-xs text-[#8C7D6B] py-6">Échangez en toute confidentialité.</p>
              )}
              {messages.map((m) => {
                const mine = m.sender_id === user?.user_id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                    <div className={`max-w-[80%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed ${mine ? "bg-[#1A1A1A] text-white" : "bg-[#FAF3EA] text-[#1A1A1A]"}`}>
                      <p className="whitespace-pre-wrap">{m.body}</p>
                      <p className={`text-[10px] mt-1 ${mine ? "text-white/50" : "text-[#8C7D6B]"}`}>
                        {new Date(m.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
            {stage !== "cancelled" && (
              <form onSubmit={sendMsg} className="pt-3 border-t border-[#FAF3EA] flex gap-2">
                <Input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Écrire un message…"
                  className="rounded-full"
                  data-testid="chat-input"
                  maxLength={2000}
                />
                <Button type="submit" disabled={sending || !draft.trim()} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white" data-testid="chat-send-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </form>
            )}
          </section>
        </div>
      </div>

      <ReviewDialog open={reviewOpen} onOpenChange={setReviewOpen} missionId={id} isTalent={isTalent} onDone={() => { loadMission(); loadReviews(); }} />
      {isTalent && (
        <AddToPortfolioDialog open={portfolioOpen} onOpenChange={setPortfolioOpen} mission={mission} />
      )}
    </AppShell>
  );
}

function StageBadge({ stage }) {
  const map = {
    scheduled: { label: "Planifiée", cls: "bg-[#FAF3EA] text-[#A26F3E] border-[#E6C29A]" },
    reminded: { label: "Imminente · 24h", cls: "bg-[#1A1A1A] text-[#D4A574]" },
    completed: { label: "Terminée", cls: "bg-[#E7F5EC] text-[#2E7D32] border-[#BFE4C5]" },
    cancelled: { label: "Annulée", cls: "bg-[#FEEFEC] text-[#A3483F] border-[#F2C0B7]" },
  };
  const s = map[stage] || map.scheduled;
  return <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${s.cls}`} data-testid={`stage-${stage}`}>{s.label}</span>;
}

function SheetField({ label, icon: Icon, children }) {
  return (
    <div>
      <Label className="caption-label flex items-center gap-1.5"><Icon className="w-3 h-3 text-[#A26F3E]" />{label}</Label>
      {children}
    </div>
  );
}

function SheetReadField({ label, icon: Icon, value }) {
  return (
    <div>
      <p className="caption-label flex items-center gap-1.5"><Icon className="w-3 h-3 text-[#A26F3E]" />{label}</p>
      <p className="mt-1 text-[#1A1A1A]">{value}</p>
    </div>
  );
}

function ReviewDialog({ open, onOpenChange, missionId, isTalent, onDone }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [onTime, setOnTime] = useState(true);
  const [pleasant, setPleasant] = useState(true);
  const [communicationClear, setCommunicationClear] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { rating, comment };
      if (isTalent) payload.communication_clear = communicationClear;
      else { payload.on_time = onTime; payload.pleasant = pleasant; }
      await api.post(`/missions/${missionId}/review`, payload);
      toast.success("Avis envoyé — merci !");
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Votre avis</DialogTitle>
          <DialogDescription>{isTalent ? "Comment s'est passée la collaboration avec la marque ?" : "Comment s'est passée la collaboration avec ce talent ?"}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="caption-label">Note</Label>
            <div className="flex gap-1 mt-2" data-testid="review-stars">
              {[1,2,3,4,5].map((n) => (
                <button key={n} type="button" onClick={() => setRating(n)} data-testid={`star-${n}`} className="transition hover:scale-110">
                  <Star className={`w-7 h-7 ${n <= rating ? "fill-[#D4A574] text-[#D4A574]" : "text-[#EADDC9]"}`} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="caption-label">Commentaire (optionnel)</Label>
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} className="rounded-xl mt-2" data-testid="review-comment" />
          </div>
          <div className="flex flex-wrap gap-2">
            {isTalent ? (
              <ToggleChip checked={communicationClear} onChange={setCommunicationClear} label="Brief clair" />
            ) : (
              <>
                <ToggleChip checked={onTime} onChange={setOnTime} label="Ponctuel·le" />
                <ToggleChip checked={pleasant} onChange={setPleasant} label="Agréable" />
              </>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Annuler</Button>
          <Button onClick={submit} disabled={submitting} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white" data-testid="review-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Envoyer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ToggleChip({ checked, onChange, label }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} className={`px-3 py-1.5 rounded-full border text-xs font-medium transition ${checked ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "bg-white text-[#6E4B2A] border-[#EADDC9] hover:border-[#C68E59]"}`}>
      {label}
    </button>
  );
}

function AddToPortfolioDialog({ open, onOpenChange, mission }) {
  const [title, setTitle] = useState(mission?.title || "");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/missions/${mission.id}/add-to-portfolio`, { title, description });
      toast.success("Ajouté à votre portfolio");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur — votre collab n'a peut-être pas d'image.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl" data-testid="portfolio-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Ajouter à mon portfolio</DialogTitle>
          <DialogDescription>L'image de la collaboration sera ajoutée à votre portfolio.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="caption-label">Titre</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="rounded-xl mt-2 h-11" />
          </div>
          <div>
            <Label className="caption-label">Description (optionnel)</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="rounded-xl mt-2" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Annuler</Button>
          <Button onClick={submit} disabled={submitting} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white" data-testid="portfolio-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ajouter"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
